import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';
import type { ChannelArea } from '@/components/criteria-builder';

/**
 * A business channel (the `channels` table). `color` is a curated palette KEY
 * (not raw hex); the FE maps it to accent classes via `lib/environment`.
 * Exactly one active channel is the `isDefault` complement. `appliesTo`
 * declares WHICH areas the channel filters (v3); `description` is the optional
 * human-readable definition shown in settings + the header dropdown tooltip.
 */
export interface Channel {
  id: string;
  name: string;
  slug: string;
  color: string;
  appliesTo: ChannelArea[];
  description: string | null;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateChannelPayload {
  name: string;
  /** Auto-derived from the name server-side when omitted. */
  slug?: string;
  color: string;
  appliesTo?: ChannelArea[];
  description?: string;
  sortOrder?: number;
}

export interface UpdateChannelPayload {
  name?: string;
  slug?: string;
  color?: string;
  appliesTo?: ChannelArea[];
  description?: string;
  isDefault?: boolean;
  sortOrder?: number;
  isActive?: boolean;
}

/**
 * Channels ordered by `sortOrder` asc. Any authenticated user may read this —
 * the header channel dropdown depends on it (active-only default). Pass
 * `{ includeInactive: true }` for the manager UI, which needs hidden channels
 * to reactivate them and to reorder the complete set. The two variants use
 * distinct query keys so the dropdown's active-only cache is never overwritten
 * by the manager's fuller list.
 */
export function useChannels(params?: { includeInactive?: boolean }) {
  const includeInactive = params?.includeInactive ?? false;
  return useQuery({
    queryKey: queryKeys.channels.list({ includeInactive }),
    queryFn: async () => {
      const { data } = await apiClient.get<Channel[]>('/api/channels', {
        params: includeInactive ? { includeInactive: true } : undefined,
      });
      return data;
    },
  });
}

/**
 * The user's accessible channels + default lens at a warehouse
 * (`GET /channels/accessible`). `unrestricted:true` means the user has no
 * channel assignment (or is admin/auditor/API-key) — the full channel list
 * applies and there is no seeded default. When `unrestricted:false`, `channels`
 * is the restricted set the header switcher renders and `defaultSlug` is the
 * per-warehouse default lens to open on. Disabled until a warehouse is known.
 */
export interface AccessibleChannels {
  unrestricted: boolean;
  channels: Channel[];
  defaultSlug: string | null;
}

export function useAccessibleChannels(warehouseId?: string) {
  return useQuery({
    queryKey: queryKeys.channels.accessible(warehouseId),
    queryFn: async () => {
      const { data } = await apiClient.get<AccessibleChannels>(
        '/api/channels/accessible',
        { params: { warehouseId } },
      );
      return data;
    },
    enabled: !!warehouseId,
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateChannelPayload) => {
      const { data } = await apiClient.post<Channel>('/api/channels', dto);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all });
    },
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...dto
    }: UpdateChannelPayload & { id: string }) => {
      const { data } = await apiClient.patch<Channel>(
        `/api/channels/${id}`,
        dto,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all });
    },
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/channels/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all });
    },
  });
}

export function useReorderChannels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelIds: string[]) => {
      await apiClient.post('/api/channels/reorder', { channelIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all });
    },
  });
}
